/**
 * MegaMotocross branding in the world: banners on posts along the start straight, a start arch over the track
 * start. Built from canvas textures (logo SVG + brand type), added to the
 * view's public scene before the warm-up so their pipelines compile with everything else.
 */
import * as THREE from 'three/webgpu';
import type { Level, Placement } from '../sim/level';
import { mergeStatic } from '../view/mergeStatic';

const LOGO = `${import.meta.env.BASE_URL}brand/megamotocross_logo_c.svg`;
export const BRAND = { orange: '#D75706', deep: '#C95400', ink: '#15130D', paper: '#F5F5F5', amber: '#F2A20C' } as const;

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/** Banner artwork: ink field, orange chevrons, the logo and the site name. */
function bannerCanvas(logo: HTMLImageElement | null, w: number, hgt: number, variant: 'banner' | 'arch' | 'van'): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = w; c.height = hgt;
  const g = c.getContext('2d')!;
  const bg = variant === 'van' ? BRAND.paper : BRAND.ink;
  g.fillStyle = bg; g.fillRect(0, 0, w, hgt);
  // Diagonal speed stripes.
  g.save();
  g.fillStyle = BRAND.orange;
  const k = hgt * 0.9;
  for (const [x0, ww, col] of [[w * 0.68, hgt * 0.5, BRAND.orange], [w * 0.68 + hgt * 0.62, hgt * 0.22, BRAND.amber], [w * 0.68 + hgt * 0.98, hgt * 0.12, BRAND.deep]] as const) {
    g.fillStyle = col;
    g.beginPath(); g.moveTo(x0 + k, 0); g.lineTo(x0 + k + ww, 0); g.lineTo(x0 + ww, hgt); g.lineTo(x0, hgt); g.closePath(); g.fill();
  }
  g.restore();
  g.fillStyle = BRAND.orange; g.fillRect(0, hgt - hgt * 0.07, w, hgt * 0.07);
  const logoH = hgt * 0.84;
  if (logo) g.drawImage(logo, hgt * 0.12, (hgt - logoH) / 2, logoH * (1024 / 998), logoH);
  const tx = hgt * 0.12 + logoH * 1.1;
  g.fillStyle = variant === 'van' ? BRAND.ink : BRAND.paper;
  g.font = `italic ${Math.round(hgt * 0.36)}px "Russo One", "Arial Black", sans-serif`;
  g.textBaseline = 'middle';
  g.fillText('MEGAMOTOCROSS', tx, hgt * 0.42);
  g.fillStyle = BRAND.orange;
  g.font = `${Math.round(hgt * 0.17)}px "Russo One", "Arial Black", sans-serif`;
  g.fillText(variant === 'arch' ? 'START · MEGAMOTOCROSS.PL' : 'MEGAMOTOCROSS.PL  ·  +48 453 442 415', tx, hgt * 0.74);
  return c;
}

/** Feather-flag artwork (portrait): ink field, orange leading edge, the logo up top and the name running upward. */
function flagCanvas(logo: HTMLImageElement | null, w: number, hgt: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = w; c.height = hgt;
  const g = c.getContext('2d')!;
  g.fillStyle = BRAND.ink; g.fillRect(0, 0, w, hgt);
  g.fillStyle = BRAND.orange; g.fillRect(0, 0, w * 0.12, hgt);
  g.fillStyle = BRAND.amber; g.fillRect(w * 0.12, 0, w * 0.03, hgt);
  const logoW = w * 0.7;
  if (logo) g.drawImage(logo, w * 0.22, w * 0.12, logoW, logoW * (998 / 1024));
  g.save();
  g.translate(w * 0.6, hgt * 0.94);
  g.rotate(-Math.PI / 2);
  g.fillStyle = BRAND.paper;
  g.font = `italic ${Math.round(w * 0.3)}px "Russo One", "Arial Black", sans-serif`;
  g.textBaseline = 'middle';
  g.fillText('MEGAMOTOCROSS', 0, 0);
  g.restore();
  return c;
}

function texture(c: HTMLCanvasElement): THREE.CanvasTexture {
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  return t;
}

export interface BrandSet {
  group: THREE.Group;
  /** Where the menu camera should look: the van and the start. */
  paddock: THREE.Vector3;
  /** Artwork for the view's paddock (Paddock.setLivery / setFlag): van side panels and feather flags. */
  art: { van: HTMLCanvasElement; flag: HTMLCanvasElement };
}

export async function addBranding(scene: THREE.Scene, level: Level, height: (x: number, z: number) => number, start: Placement): Promise<BrandSet> {
  await document.fonts?.load('40px "Russo One"').catch(() => null);
  const logo = await loadImage(LOGO);
  const group = new THREE.Group();
  group.name = 'megamotocross-branding';
  const bannerTex = texture(bannerCanvas(logo, 2048, 416, 'banner'));
  const archTex = texture(bannerCanvas(logo, 2048, 320, 'arch'));
  const bannerMat = new THREE.MeshStandardNodeMaterial({ map: bannerTex, roughness: 0.75 }); // front-sided: each face has its own quad, text reads right from both sides
  const archMat = new THREE.MeshStandardNodeMaterial({ map: archTex, roughness: 0.7 });
  const postMat = new THREE.MeshStandardNodeMaterial({ color: 0x2a2724, roughness: 0.5, metalness: 0.6 });
  const orangeMat = new THREE.MeshStandardNodeMaterial({ color: 0xd75706, roughness: 0.45, metalness: 0.1 });
  const inkMat = new THREE.MeshStandardNodeMaterial({ color: 0x15130d, roughness: 0.4, metalness: 0.2 });
  const mesh = (g: THREE.BufferGeometry, m: THREE.Material) => { const o = new THREE.Mesh(g, m); o.castShadow = true; o.receiveShadow = true; return o; };

  const place = (o: THREE.Object3D, x: number, z: number, yaw: number, lift = 0) => {
    o.position.set(x, height(x, z) + lift, z);
    o.rotation.y = yaw;
    group.add(o);
  };

  // Banner on two posts; its face looks along local +z.
  const banner = (x: number, z: number, faceYaw: number) => {
    const b = new THREE.Group();
    const w = 4.8, hh = 0.98;
    const panel = mesh(new THREE.PlaneGeometry(w, hh), bannerMat);
    panel.position.y = 0.5 + hh / 2;
    // Back face: a second quad turned round (a double-sided plane showed the artwork mirrored from behind).
    const backPanel = panel.clone(); backPanel.rotation.y = Math.PI; backPanel.position.z = -0.005;
    const post = new THREE.CylinderGeometry(0.035, 0.035, 1.6, 8).translate(0, 0.8, 0);
    const l = mesh(post, postMat), r = mesh(post, postMat);
    l.position.x = -w / 2; r.position.x = w / 2;
    b.add(panel, backPanel, l, r);
    place(b, x, z, faceYaw);
  };

  const line = level.line;
  const fwd = (h: number) => ({ x: Math.sin(h), z: Math.cos(h) });
  const f = fwd(start.heading);
  const left = { x: Math.cos(start.heading), z: -Math.sin(start.heading) };
  const yawFacing = (fromX: number, fromZ: number, toX: number, toZ: number) => Math.atan2(toX - fromX, toZ - fromZ);

  if (line) {
    const s0 = line.closest(start.x, start.z).s;
    const corridor = level.def.forest?.corridor;
    const side = corridor ? Math.min(level.halfWidth + 2.2, corridor / 2 + 1) : level.halfWidth + 3.2;
    let k = 0;
    for (const ds of [-26, -12, 14, 30, 48]) {
      const p = line.pointAt(s0 + ds), t = line.tangentAt(s0 + ds);
      const sgn = k++ % 2 === 0 ? 1 : -1;
      const nx = t.z * sgn, nz = -t.x * sgn; // left normal when sgn = 1
      const bx = p.x + nx * side, bz = p.z + nz * side;
      banner(bx, bz, yawFacing(bx, bz, p.x, p.z));
    }
    // Start arch across the track a few metres ahead of the start.
    const a = line.pointAt(s0 + 7), at = line.tangentAt(s0 + 7);
    const span = (corridor ? Math.min(level.halfWidth + 1.5, corridor / 2) : level.halfWidth + 1.8) * 2;
    const arch = new THREE.Group();
    const pillar = new THREE.BoxGeometry(0.45, 4.6, 0.45).translate(0, 2.3, 0);
    const pl = mesh(pillar, orangeMat), pr = mesh(pillar, orangeMat);
    pl.position.x = -span / 2; pr.position.x = span / 2;
    const beam = mesh(new THREE.BoxGeometry(span + 0.45, 0.95, 0.3), inkMat);
    beam.position.y = 4.6;
    const face = mesh(new THREE.PlaneGeometry(span * 0.96, span * 0.96 * (320 / 2048)), archMat);
    face.position.set(0, 4.6, 0.16);
    const back = face.clone(); back.position.z = -0.16; back.rotation.y = Math.PI;
    arch.add(pl, pr, beam, face, back);
    place(arch, a.x, a.z, Math.atan2(at.x, at.z) + Math.PI);
  } else {
    // Ring: banners round the edge of the pad, facing its centre.
    const cx = 0, cz = 0, r = 58;
    for (const ang of [-2.1, -1.55, -1.0, 0.9, 1.6, 2.4]) {
      const bx = cx + Math.cos(ang) * r, bz = cz + Math.sin(ang) * r;
      banner(bx, bz, yawFacing(bx, bz, cx, cz));
    }
  }

  // The paddock itself (vehicles, tents, lighting) belongs to src/view; this module only adds brand decals.
  const px = start.x + left.x * 9 - f.x * 4, pz = start.z + left.z * 9 - f.z * 4;
  mergeStatic(group); // one draw per material instead of one per banner part
  scene.add(group);
  const art = { van: bannerCanvas(logo, 2048, 416, 'van'), flag: flagCanvas(logo, 512, 1780) };
  return { group, paddock: new THREE.Vector3(px, height(px, pz) + 1.2, pz), art };
}
